import React, { useState, useEffect } from 'react';
import { db } from '../firebase/firestore';
import { collection, getDocs, query, doc, setDoc, getDoc } from 'firebase/firestore';
import moment from 'moment';
import Spinner from './Spinner';
import { useUser } from '../context/userContext';

const StatisticsPanel = () => {
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeUsersLast24Hours, setActiveUsersLast24Hours] = useState(0);
  const [activeUsersLast1Minute, setActiveUsersLast1Minute] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [uniqueOrderUsers, setUniqueOrderUsers] = useState(0);
  const [ordersList, setOrdersList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [usdRate, setUsdRate] = useState(0);       // курс USD
  const [usdInput, setUsdInput] = useState('');    // ввод нового курса
  const [savingRate, setSavingRate] = useState(false);

  const userContext = useUser(); // безопасно
  const adminUsername = userContext?.username || 'Admin';

  useEffect(() => {
    fetchStatistics();
    fetchUsdRate();
  }, []);

  const fetchUsdRate = async () => {
    try {
      const rateDocRef = doc(db, 'settings', 'exchangeRates');
      const rateSnap = await getDoc(rateDocRef);
      if (rateSnap.exists()) {
        const data = rateSnap.data();
        setUsdRate(data.USD_RUB || 0);
        setUsdInput(data.USD_RUB?.toString() || '');
      }
    } catch (err) {
      console.error('Ошибка при получении курса USD:', err);
    }
  };

  const saveUsdRate = async () => {
    if (!usdInput || isNaN(parseFloat(usdInput))) return;
    setSavingRate(true);
    try {
      const rateDocRef = doc(db, 'settings', 'exchangeRates');
      await setDoc(rateDocRef, { USD_RUB: parseFloat(usdInput) }, { merge: true });
      setUsdRate(parseFloat(usdInput));
    } catch (err) {
      console.error('Ошибка при сохранении курса USD:', err);
    } finally {
      setSavingRate(false);
    }
  };

  const fetchStatistics = async () => {
    const last24Hours = moment().subtract(24, 'hours').toDate();
    const last1Minute = moment().subtract(1, 'minute').toDate();

    try {
      const usersQuery = query(collection(db, "telegramUsers"));
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(doc => doc.data());

      setTotalUsers(usersData.length);

      const active24h = usersData.filter(user => user.lastActive && user.lastActive.toDate() > last24Hours).length;
      const active1m = usersData.filter(user => user.lastActive && user.lastActive.toDate() > last1Minute).length;
      setActiveUsersLast24Hours(active24h);
      setActiveUsersLast1Minute(active1m);

      // === Orders ===
      let allOrders = [];
      for (let userDoc of usersSnapshot.docs) {
        const userId = userDoc.id;
        const userData = userDoc.data(); 
        const username = userData.username || '-';

        const ordersRef = collection(db, `telegramUsers/${userId}/orders`);
        const ordersSnapshot = await getDocs(ordersRef);
        const ordersData = ordersSnapshot.docs.map(doc => ({
          ...doc.data(),
          userId,
          username
        }));
        allOrders = allOrders.concat(ordersData);
      }

      setOrdersList(allOrders);
      setTotalOrders(allOrders.length);

      const revenueSum = allOrders.reduce((acc, order) => acc + (parseFloat(order.amountInKopecks || 0) / 100), 0);
      setTotalRevenue(revenueSum);

      const uniqueUsers = new Set(allOrders.map(o => o.userId)).size;
      setUniqueOrderUsers(uniqueUsers);

    } catch (error) {
      console.error('Ошибка при получении статистики:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (typeof num !== "number") return "Invalid";
    if (num < 1 && num.toString().split('.')[1]?.length > 3) {
      return num.toFixed(6).replace(/0+$/, '');
    }
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <>
      {loading ? (
        <Spinner />
      ) : (
        <div className="w-full flex flex-col space-y-4 h-[100vh] pt-2 overflow-y-auto pb-[120px]">

          {/* === Блок управления курсом USD → RUB === 
          <div className="bg-cards p-3 rounded-[8px] flex flex-col sm:flex-row items-center gap-3 w-full">
            <span className="text-white font-semibold">USD → RUB:</span>
            <input
              type="number"
              step="0.01"
              value={usdInput}
              onChange={(e) => setUsdInput(e.target.value)}
              className="p-2 rounded-md w-[100px] text-black"
            />
            <button
              onClick={saveUsdRate}
              disabled={savingRate}
              className="bg-green-500 text-white px-4 py-2 rounded-md disabled:opacity-50"
            >
              {savingRate ? 'Saving...' : 'Save'}
            </button>
            <span className="text-gray-300 ml-2">Current: {usdRate}</span>
          </div>*/}

          {/* === Статистика сверху === */}
          <div className="w-full flex justify-start items-start flex-wrap gap-3">
            {[
              { title: 'Total Users', count: totalUsers },
              { title: 'Active 24h', count: activeUsersLast24Hours },
              { title: 'Online Now', count: activeUsersLast1Minute },
              { title: 'Orders', count: totalOrders },
              { title: 'Revenue ₽', count: formatNumber(totalRevenue) },
              { title: 'Buyers', count: uniqueOrderUsers },
              { title: 'Admin', count: adminUsername },
            ].map((stats, index) => (
              <div
                key={index}
                className="bg-cards p-3 rounded-[8px] w-[47%] sm:w-[30%] h-[100px] flex flex-col justify-center space-y-2"
                style={{ backgroundColor: index % 2 === 0 ? '#1f2937' : '#111827' }}
              >
                <h2 className="text-[14px] sm:text-[16px] font-semibold text-[#bdbdbd]">
                  {stats.title}
                </h2>
                <span className="text-[16px] sm:text-[18px] text-[#fff] font-bold">
                  {stats.count}
                </span>
              </div>
            ))}
          </div>

          {/* === Список заказов === */}
          <h2 className="text-[16px] sm:text-[18px] font-semibold text-[#bdbdbd] mt-4 mb-2">
            Orders List
          </h2>
          <div className="flex flex-col gap-2">
            {ordersList.map((order, index) => (
              <div
                key={index}
                className="bg-cards p-3 rounded-[8px] w-full flex flex-col sm:flex-row justify-between items-start sm:items-center gap-1"
                style={{ backgroundColor: index % 2 === 0 ? '#1f2937' : '#111827' }}
              >
                <span className="text-[12px] sm:text-[14px] text-[#fff] font-medium sm:w-[12%] break-words">
                  <strong>Telegram ID:</strong> {order.userId}
                </span>
                <span className="text-[12px] sm:text-[14px] text-[#fff] font-medium sm:w-[12%]">
                  <strong>Username:</strong> {order.username || '-'}
                </span>
                <span className="text-[12px] sm:text-[14px] text-[#fff] font-medium sm:w-[12%]">
                  <strong>Amount:</strong> {(parseFloat(order.amountInKopecks) / 100).toLocaleString()} ₽
                </span>
                <span className="text-[12px] sm:text-[14px] text-[#fff] font-medium sm:w-[20%]">
                  <strong>Date:</strong> {order.createdAt?.toDate ? moment(order.createdAt.toDate()).format('DD.MM.YYYY HH:mm') : order.createdAt}
                </span>
                <span className="text-[12px] sm:text-[14px] text-[#fff] font-medium sm:w-[28%] break-words">
                  <strong>Description:</strong> {order.description || '-'}
                </span>
                {order.tinkoff && (
                  <span className="text-[12px] sm:text-[14px] text-[#10b981] font-bold sm:w-[10%] text-center">
                    Tinkoff
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default StatisticsPanel;
